import React, { useState, useEffect } from 'react';
import {
  Search, TrendingUp, Ban, DollarSign, Target, AlertTriangle,
  RefreshCw, Download, Plus, Filter, CheckCircle
} from 'lucide-react';
import { getGoogleAdsClient, getDateRange, type SearchTerm } from '@/lib/google-ads-client';
import { createGoogleAdsAIOptimizer, type NegativeKeyword } from '@/lib/google-ads-ai-optimizer';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { useGoogleAdsCompany, GOOGLE_ADS_COMPANY_OPTIONS } from '@/hooks/useGoogleAdsCompany';

interface SearchTermWithStatus extends SearchTerm {
  isNegative?: boolean;
  wastedSpend?: number;
  selected?: boolean;
}

export default function SearchQueryAnalyzerPage() {
  const [searchTerms, setSearchTerms] = useState<SearchTermWithStatus[]>([]);
  const [negativeKeywords, setNegativeKeywords] = useState<NegativeKeyword[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'all' | 'converting' | 'wasted'>('all');
  const [dateRange, setDateRange] = useState(30);
  const { company, setCompany } = useGoogleAdsCompany();

  useEffect(() => {
    if (!company) return;
    loadSearchTerms();
  }, [dateRange, company]);

  const loadSearchTerms = async () => {
    try {
      setLoading(true);

      if (!company) {
        toast.error('No company selected');
        return;
      }

      const { data: mappings } = await supabase
        .from('google_ads_customer_mapping')
        .select('*')
        .eq('company', company)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (!mappings) {
        toast.error('No Google Ads account connected');
        return;
      }

      const customerId = mappings.customer_id;
      const client = getGoogleAdsClient(customerId);
      const range = getDateRange(dateRange);

      // Get search terms
      const terms = await client.getSearchTerms(customerId, { dateRange: range });

      // Get AI recommendations for negative keywords
      const optimizer = createGoogleAdsAIOptimizer(client);
      const report = await optimizer.generateOptimizationReport(customerId, range);
      
      setNegativeKeywords(report.negativeKeywords);

      // Mark terms as negative candidates
      const termsWithStatus = terms.map(term => {
        const negCandidate = report.negativeKeywords.find(
          nk => nk.searchTerm === term.searchTerm
        );

        return {
          ...term,
          isNegative: !!negCandidate,
          wastedSpend: negCandidate?.wastedSpend || 0,
          selected: false,
        };
      });

      setSearchTerms(termsWithStatus);

    } catch (error) {
      console.error('Error loading search terms:', error);
      toast.error('Failed to load search queries');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredTerms = () => {
    if (view === 'converting') {
      return searchTerms.filter(t => t.conversions > 0);
    }
    if (view === 'wasted') {
      return searchTerms.filter(t => t.isNegative);
    }
    return searchTerms;
  };

  const filteredTerms = getFilteredTerms();

  const stats = {
    total: searchTerms.length,
    converting: searchTerms.filter(t => t.conversions > 0).length,
    wasted: searchTerms.filter(t => t.isNegative).length,
    totalWaste: searchTerms.reduce((sum, t) => sum + (t.wastedSpend || 0), 0),
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-lg">Analyzing search queries...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex justify-end">
        <select
          value={company}
          onChange={(e) => setCompany(e.target.value as 'hales' | 'myccs' | 'abraham')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {GOOGLE_ADS_COMPANY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Search Query Analyzer</h1>
        <p className="text-gray-600">Analyze actual user searches to optimize targeting</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Total Queries</span>
            <Search className="h-5 w-5 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Converting</span>
            <Target className="h-5 w-5 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-green-600">{stats.converting}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Wasted Queries</span>
            <Ban className="h-5 w-5 text-red-500" />
          </div>
          <div className="text-3xl font-bold text-red-600">{stats.wasted}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Wasted Spend</span>
            <DollarSign className="h-5 w-5 text-orange-500" />
          </div>
          <div className="text-3xl font-bold text-orange-600">
            {formatCurrency(stats.totalWaste)}
          </div>
        </div>
      </div>

      {/* View Tabs & Controls */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <button
                onClick={() => setView('all')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  view === 'all'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                All ({stats.total})
              </button>
              <button
                onClick={() => setView('converting')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  view === 'converting'
                    ? 'bg-green-100 text-green-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Converting ({stats.converting})
              </button>
              <button
                onClick={() => setView('wasted')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  view === 'wasted'
                    ? 'bg-red-100 text-red-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Wasted Spend ({stats.wasted})
              </button>
            </div>

            <div className="flex gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(Number(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
              <button
                onClick={loadSearchTerms}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search Terms List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Search Queries ({filteredTerms.length})
          </h2>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredTerms.slice(0, 100).map((term, idx) => {
            const cost = term.costMicros / 1000000;
            const ctr = term.ctr * 100;

            return (
              <div
                key={idx}
                className={`p-6 ${term.isNegative ? 'bg-red-50' : 'hover:bg-gray-50'} transition-colors`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{term.searchTerm}</h3>
                      {term.addedAsKeyword && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                          KEYWORD
                        </span>
                      )}
                      {term.isNegative && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                          WASTE
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Impressions</div>
                        <div className="text-sm font-semibold">{term.impressions.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Clicks</div>
                        <div className="text-sm font-semibold">{term.clicks}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Cost</div>
                        <div className="text-sm font-semibold">{formatCurrency(cost)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Conversions</div>
                        <div className={`text-sm font-semibold ${term.conversions > 0 ? 'text-green-600' : 'text-gray-900'}`}>
                          {term.conversions}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">CTR</div>
                        <div className="text-sm font-semibold">{ctr.toFixed(2)}%</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredTerms.length === 0 && (
          <div className="p-12 text-center text-gray-500">
            <Search className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No search queries found</p>
          </div>
        )}
      </div>
    </div>
  );
}
